import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, Mail, Phone, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  prenom: "",
  nom: "",
  cabinet: "",
  email: "",
  telephone: "",
  adresse: "",
  message: "",
  consent: false,
};

export default function ContactForm() {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.consent) {
      toast.error("Merci de cocher le consentement RGPD pour continuer.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, data);
      setSent(true);
      toast.success("Demande envoyée. Nous vous recontactons sous 24h.");
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", { method: "contact_form" });
      }
      setData(initial);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || "Erreur. Merci de réessayer.";
      toast.error(typeof msg === "string" ? msg : "Erreur. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-spi-red mb-4">
            Contact
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-spi-blue font-bold">
            Parlons de votre couverture.
          </h2>
          <p className="mt-6 text-slate-600 leading-relaxed">
            Un conseiller vous recontacte sous 24h ouvrées pour un échange gratuit et sans engagement.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <Mail className="w-5 h-5 text-spi-red mt-1" strokeWidth={1.75} />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Email</div>
                <a href="mailto:contact@sp-insurance.ch" className="text-spi-blue hover:underline font-medium">
                  contact@sp-insurance.ch
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-5 h-5 text-spi-red mt-1" strokeWidth={1.75} />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Téléphone</div>
                <a href="tel:+41225598684" className="text-spi-blue hover:underline font-medium">
                  +41 22 559 86 84
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-spi-red mt-1" strokeWidth={1.75} />
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Adresse</div>
                <div className="text-spi-blue font-medium">Pl. de Cornavin 14, 1201 Genève, Suisse</div>
                <div className="text-xs text-slate-500 mt-2">
                  Lundi – Vendredi : 09:00 – 18:00<br />
                  Samedi et Dimanche : Fermé
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-white border-2 border-slate-100 rounded-2xl p-6 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            {sent ? (
              <div className="text-center py-8" data-testid="contact-success">
                <CheckCircle2 className="w-12 h-12 text-emerald-700 mx-auto" strokeWidth={1.5} />
                <h3 className="mt-4 font-display text-2xl text-spi-blue font-medium">
                  Demande envoyée avec succès
                </h3>
                <p className="mt-3 text-slate-700 text-sm leading-relaxed max-w-md mx-auto">
                  Nous avons bien reçu votre demande de subside concernant votre
                  assurance maladie. Un conseiller vous recontactera sous 24h ouvrées.
                </p>
                <p className="mt-3 text-[10px] text-slate-500 leading-snug max-w-md mx-auto">
                  Conformément au RGPD/LPD, vos données sont utilisées uniquement
                  pour traiter votre demande et ne sont jamais cédées à des tiers.
                </p>
                <Button
                  variant="outline"
                  className="mt-6"
                  onClick={() => setSent(false)}
                  data-testid="contact-new"
                >
                  Envoyer une autre demande
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="prenom">Prénom *</Label>
                    <Input
                      id="prenom"
                      data-testid="contact-input-prenom"
                      required
                      value={data.prenom}
                      onChange={(e) => set("prenom", e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="nom">Nom *</Label>
                    <Input
                      id="nom"
                      data-testid="contact-input-nom"
                      required
                      value={data.nom}
                      onChange={(e) => set("nom", e.target.value)}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-input-email"
                      required
                      value={data.email}
                      onChange={(e) => set("email", e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="telephone">Téléphone *</Label>
                    <Input
                      id="telephone"
                      data-testid="contact-input-telephone"
                      required
                      value={data.telephone}
                      onChange={(e) => set("telephone", e.target.value)}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="cabinet">Cabinet / Société (optionnel)</Label>
                  <Input
                    id="cabinet"
                    data-testid="contact-input-cabinet"
                    value={data.cabinet}
                    onChange={(e) => set("cabinet", e.target.value)}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="adresse">Adresse à Genève (optionnel)</Label>
                  <Input
                    id="adresse"
                    data-testid="contact-input-adresse"
                    value={data.adresse}
                    onChange={(e) => set("adresse", e.target.value)}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Votre message</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-input-message"
                    rows={4}
                    value={data.message}
                    onChange={(e) => set("message", e.target.value)}
                    className="mt-2"
                    placeholder="Décrivez votre situation ou votre besoin…"
                  />
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F9F8F6] border border-slate-200">
                  <Checkbox
                    id="consent"
                    data-testid="contact-consent"
                    checked={data.consent}
                    onCheckedChange={(v) => set("consent", !!v)}
                    className="mt-1"
                  />
                  <Label htmlFor="consent" className="text-xs text-slate-600 leading-relaxed font-normal cursor-pointer">
                    J'accepte que mes données soient utilisées par Swiss Premium Insurance
                    pour me recontacter dans le cadre de ma demande, conformément
                    à la <a href="/politique-confidentialite" className="underline">politique de confidentialité</a>.
                    Je peux retirer mon consentement à tout moment.
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="w-full bg-spi-blue hover:bg-spi-blue-dark text-white h-12 font-semibold"
                >
                  {loading ? "Envoi en cours…" : "Envoyer ma demande"}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  Vos données sont chiffrées et ne sont jamais cédées à des tiers.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
